import { type BossAbilityKind, MOBS } from '@shefira/shared';

import { DRAGON_CLIPS, SHEFIRA_COMBAT_CLIPS } from '../cinematics/dragon-clips.js';
import { ShefiraGait } from './shefira-gait.js';

/**
 * Shefira in her Aire (ADR 0068), as the client draws her: which clip and
 * frame she shows, where her feet are, how high she flies and where her
 * shadow lies, at any instant. Pure: fed the server's events with the
 * time they came, read at the frame's time. Nothing here decides the fight;
 * the server said she rises, dives or lands, this only stages it.
 *
 * On the ground she idles, walks with her wings folded while the server
 * moves her (shefira-gait.ts: her feet tread at her pace), and each shape
 * the server marks plays the clip that throws it, timed so its blow frame falls when the shape lands. In the
 * sky she hovers high over a figure of eight round where she rose, her
 * shadow on the ground under her; a dive glides the shadow onto its target
 * (it darkens and grows: it closes on them), then she falls on it, stands,
 * and climbs back. At the story's end the sword strikes and she flees.
 */

export const SHEFIRA_ART_CLIPS = { ...DRAGON_CLIPS, ...SHEFIRA_COMBAT_CLIPS } as const;
export type ShefiraClip = keyof typeof SHEFIRA_ART_CLIPS;

export interface Point {
  x: number;
  y: number;
}

/** How high she flies over her shadow, in world pixels: over the heads of everyone. */
export const FLIGHT_ALTITUDE = 100;
/** Her figure of eight in the sky: across the arena, a little less deep than wide. */
const FLIGHT_RX = 7 * 16;
const FLIGHT_RY = 4 * 16;
const FLIGHT_PERIOD_MS = 14_000;
/** A dive: the last of the windup she falls, then stands, then climbs again. */
const DIVE_FALL_MS = 400;
const DIVE_CLIMB_MS = 800;
/** Coming down: the glide back over where she rose, before the landing clip. */
const LANDING_CLIP_MS = DRAGON_CLIPS.landing.frames * DRAGON_CLIPS.landing.ms;
const TAKEOFF_CLIP_MS = DRAGON_CLIPS.takeoff.frames * DRAGON_CLIPS.takeoff.ms;
/** Fleeing with the sword in her flank: gone from the arena's sky after this rise. */
const FLEE_RISE_MS = 1200;
const FLEE_HEIGHT = 420;
/** Slain: her last roar, fading out. */
const FALL_FADE_MS = 1000;

/**
 * The clip each ground shape throws, and the frame its blow lands on
 * (dragon-clips.ts): the breath's fire at 8, the tail's sweep crossing at
 * 4, the wings' beat at 6; the embers' rain and the ring of fire are a roar.
 */
const SHAPE_CLIPS: Partial<Record<BossAbilityKind, { clip: ShefiraClip; impact: number }>> = {
  line: { clip: 'breath', impact: 8 },
  ring: { clip: 'tail', impact: 4 },
  nova: { clip: 'wings', impact: 6 },
  rain: { clip: 'roar', impact: 12 },
  cross: { clip: 'roar', impact: 12 },
  slam: { clip: 'wings', impact: 6 },
};

/** What to draw this frame. */
export interface ShefiraPose {
  clip: ShefiraClip;
  frame: number;
  /** Where her canvas's feet go, in world pixels, the flight's height taken off. */
  feet: Point;
  /** In the sky: drawn over everything, out of reach. */
  high: boolean;
  /** Her shadow on the ground, when she is off it; scale and darkness. */
  shadow: { at: Point; scale: number; alpha: number } | null;
  alpha: number;
  /** Nothing to draw: she has gone. */
  hidden: boolean;
}

type Stage =
  | { kind: 'ground' }
  | { kind: 'rising'; at: number; ms: number }
  | { kind: 'flying'; since: number }
  | { kind: 'landing'; at: number; ms: number; from: Point }
  | { kind: 'diving'; at: number; landsAt: number; from: Point; centre: Point; since: number }
  | { kind: 'fleeing'; at: number }
  | { kind: 'fallen'; at: number };

interface Attack {
  clip: ShefiraClip;
  impact: number;
  markedAt: number;
  landsAt: number;
}

/** The frame a clip shows `elapsed` ms in: a loop wraps, a one-shot holds its last. */
export function clipFrame(clip: ShefiraClip, elapsed: number): number {
  const { frames, ms, loop } = SHEFIRA_ART_CLIPS[clip];
  const index = Math.floor(Math.max(0, elapsed) / ms);
  return loop ? index % frames : Math.min(frames - 1, index);
}

function clipMs(clip: ShefiraClip): number {
  const { frames, ms } = SHEFIRA_ART_CLIPS[clip];
  return frames * ms;
}

/**
 * An attack's frame at `now`: before its blow, the frames up to `impact`
 * at the clip's pace ending on the landing, or squeezed into a windup too
 * short for them; after it, the rest at the clip's pace. Null once over.
 */
export function attackFrame(attack: Attack, now: number): number | null {
  const { clip, impact, markedAt, landsAt } = attack;
  const pace = SHEFIRA_ART_CLIPS[clip].ms;
  const windup = Math.max(1, landsAt - markedAt);
  if (now < landsAt) {
    const lead = impact * pace;
    if (lead <= windup) {
      const start = landsAt - lead;
      return now < start ? null : Math.min(impact - 1, Math.floor((now - start) / pace));
    }
    return Math.min(impact - 1, Math.floor(((now - markedAt) / windup) * impact));
  }
  const after = impact + Math.floor((now - landsAt) / pace);
  return after >= SHEFIRA_ART_CLIPS[clip].frames ? null : after;
}

const lerp = (a: number, b: number, k: number): number => a + (b - a) * Math.max(0, Math.min(1, k));
const lerpPoint = (a: Point, b: Point, k: number): Point => ({
  x: lerp(a.x, b.x, k),
  y: lerp(a.y, b.y, k),
});
const round = (p: Point): Point => ({ x: Math.round(p.x), y: Math.round(p.y) });
/** Smooth in and out, for a glide that neither starts nor stops with a jerk. */
const ease = (k: number): number => {
  const t = Math.max(0, Math.min(1, k));
  return t * t * (3 - 2 * t);
};

export class ShefiraMotion {
  private stage: Stage = { kind: 'ground' };
  private attack: Attack | null = null;
  private readonly gait = new ShefiraGait(MOBS.shefira.tilesPerSecond);

  /** Where she flies round: the tile she rose from, as the scene draws it. */
  constructor(private anchor: Point) {
    this.gait.place(anchor, 0);
  }

  get airborne(): boolean {
    const kind = this.stage.kind;
    return kind !== 'ground' && kind !== 'fallen';
  }

  get fleeing(): boolean {
    return this.stage.kind === 'fleeing';
  }

  /** While she flees with the sword: how long before she is gone from the sky. */
  fleeLeft(now: number): number {
    const stage = this.stage;
    if (stage.kind !== 'fleeing') return 0;
    return Math.max(0, stage.at + clipMs('hurt') + clipMs('takeoff-wounded') + FLEE_RISE_MS - now);
  }

  /** Fleeing or slain: drawn to the end of it even once her entity has left the room. */
  get ending(): boolean {
    return this.stage.kind === 'fleeing' || this.stage.kind === 'fallen';
  }

  /** The server's state: a player who joins mid-flight sees her up there at once. */
  sync(airborne: boolean, now: number): void {
    if (this.stage.kind === 'fleeing' || this.stage.kind === 'fallen') return;
    if (airborne && this.stage.kind === 'ground') {
      this.stage = { kind: 'flying', since: now };
      this.attack = null;
    }
    if (!airborne && this.stage.kind !== 'ground' && this.stage.kind !== 'landing') {
      this.stage = { kind: 'ground' };
    }
  }

  /**
   * Her tile on the ground, as the scene draws it: she walks there at her
   * pace, and flies round where she stood when she rose.
   */
  setAnchor(anchor: Point, now: number): void {
    if (!this.airborne) this.anchor = this.gait.follow(anchor, now);
  }

  takeoff(ms: number, now: number): void {
    if (this.stage.kind === 'fleeing' || this.stage.kind === 'fallen') return;
    this.attack = null;
    this.stage = { kind: 'rising', at: now, ms: Math.max(ms, TAKEOFF_CLIP_MS) };
  }

  landing(ms: number, now: number): void {
    if (this.stage.kind === 'fleeing' || this.stage.kind === 'fallen') return;
    const from = this.groundAt(now);
    this.stage = { kind: 'landing', at: now, ms: Math.max(ms, LANDING_CLIP_MS), from };
  }

  /** A shape marked by her: on the ground, the clip that throws it. */
  shape(kind: BossAbilityKind, ms: number, now: number): void {
    if (this.stage.kind !== 'ground') return;
    const plan = SHAPE_CLIPS[kind];
    if (plan === undefined) return;
    this.attack = { ...plan, markedAt: now, landsAt: now + ms };
  }

  /** The ring of fire marked (phase 3): she roars it. */
  ring(ms: number, now: number): void {
    this.shape('rain', ms, now);
  }

  /** A dive marked on a player: `centre` in world pixels (the feet of that tile). */
  dive(centre: Point, ms: number, now: number): void {
    if (this.stage.kind === 'fleeing' || this.stage.kind === 'fallen') return;
    const since =
      this.stage.kind === 'flying' || this.stage.kind === 'diving' ? this.stage.since : now;
    this.stage = {
      kind: 'diving',
      at: now,
      landsAt: now + ms,
      from: this.groundAt(now),
      centre: { ...centre },
      since,
    };
  }

  /** Ser Givrecoeur's sword (the story's end): she is struck, then flees. */
  sword(now: number): void {
    this.attack = null;
    this.stage = { kind: 'fleeing', at: now };
  }

  /** Slain (a raid won). */
  fall(now: number): void {
    if (this.stage.kind === 'fallen' || this.stage.kind === 'fleeing') return;
    this.attack = null;
    this.stage = { kind: 'fallen', at: now };
  }

  /** Where the flight's figure of eight is at `now`, on the ground. */
  private pathAt(since: number, now: number): Point {
    const t = ((now - since) / FLIGHT_PERIOD_MS) * Math.PI * 2;
    return {
      x: this.anchor.x + FLIGHT_RX * Math.sin(t),
      y: this.anchor.y + (FLIGHT_RY * Math.sin(2 * t)) / 2,
    };
  }

  /** The ground point under her now, whatever she does. */
  private groundAt(now: number): Point {
    const stage = this.stage;
    switch (stage.kind) {
      case 'flying':
        return this.pathAt(stage.since, now);
      case 'diving':
        return this.pose(now).shadow?.at ?? stage.centre;
      default:
        return this.pose(now).shadow?.at ?? { ...this.anchor };
    }
  }

  /** What to draw at `now`; moves on to the next stage when one is over. */
  pose(now: number): ShefiraPose {
    this.advance(now);
    const stage = this.stage;
    const still = (clip: ShefiraClip, frame: number, feet: Point = this.anchor): ShefiraPose => ({
      clip,
      frame,
      feet: round(feet),
      high: false,
      shadow: null,
      alpha: 1,
      hidden: false,
    });
    const sky = (
      ground: Point,
      altitude: number,
      clip: ShefiraClip,
      frame: number,
      shadow = 0,
    ): ShefiraPose => {
      const up = Math.max(0, Math.min(1, altitude / FLIGHT_ALTITUDE));
      return {
        clip,
        frame,
        feet: round({ x: ground.x, y: ground.y - altitude }),
        high: altitude > 0,
        shadow: {
          at: round(ground),
          scale: Math.round((1 - 0.3 * up + 0.3 * shadow) * 10) / 10,
          alpha: 0.55 - 0.2 * up + 0.2 * shadow,
        },
        alpha: 1,
        hidden: false,
      };
    };
    switch (stage.kind) {
      case 'ground': {
        const attack = this.attack;
        const frame = attack === null ? null : attackFrame(attack, now);
        if (attack !== null && frame !== null) {
          this.gait.spread();
          return still(attack.clip, frame);
        }
        const legs = this.gait.clip(now);
        if (legs !== null) return still(legs.clip, legs.frame);
        return still('idle', clipFrame('idle', now));
      }
      case 'rising': {
        const t = now - stage.at;
        if (t < TAKEOFF_CLIP_MS) return still('takeoff', clipFrame('takeoff', t));
        const climb = Math.max(1, stage.ms - TAKEOFF_CLIP_MS);
        const altitude = FLIGHT_ALTITUDE * ease((t - TAKEOFF_CLIP_MS) / climb);
        return sky(this.anchor, altitude, 'hover', clipFrame('hover', t));
      }
      case 'flying':
        return sky(
          this.pathAt(stage.since, now),
          FLIGHT_ALTITUDE,
          'hover',
          clipFrame('hover', now),
        );
      case 'landing': {
        const t = now - stage.at;
        const glide = Math.max(1, stage.ms - LANDING_CLIP_MS);
        if (t < glide) {
          const k = ease(t / glide);
          return sky(
            lerpPoint(stage.from, this.anchor, k),
            FLIGHT_ALTITUDE * (1 - k),
            'hover',
            clipFrame('hover', now),
          );
        }
        return still('landing', clipFrame('landing', t - glide));
      }
      case 'diving': {
        const glideEnd = Math.max(stage.at, stage.landsAt - DIVE_FALL_MS);
        if (now < glideEnd) {
          const k = ease((now - stage.at) / Math.max(1, glideEnd - stage.at));
          return sky(
            lerpPoint(stage.from, stage.centre, k),
            FLIGHT_ALTITUDE,
            'hover',
            clipFrame('hover', now),
            k,
          );
        }
        if (now < stage.landsAt) {
          const k = (now - glideEnd) / Math.max(1, stage.landsAt - glideEnd);
          const frame = Math.min(4, Math.floor(k * 5));
          return sky(stage.centre, FLIGHT_ALTITUDE * (1 - k), 'dive', frame, 1);
        }
        const after = now - stage.landsAt;
        const standMs = (SHEFIRA_COMBAT_CLIPS.dive.frames - 5) * SHEFIRA_COMBAT_CLIPS.dive.ms;
        if (after < standMs) {
          const frame = Math.min(
            SHEFIRA_COMBAT_CLIPS.dive.frames - 1,
            5 + Math.floor(after / SHEFIRA_COMBAT_CLIPS.dive.ms),
          );
          return still('dive', frame, stage.centre);
        }
        if (after < standMs + TAKEOFF_CLIP_MS)
          return still('takeoff', clipFrame('takeoff', after - standMs), stage.centre);
        const k = ease((after - standMs - TAKEOFF_CLIP_MS) / DIVE_CLIMB_MS);
        const back = this.pathAt(stage.since, now);
        return sky(
          lerpPoint(stage.centre, back, k),
          FLIGHT_ALTITUDE * k,
          'hover',
          clipFrame('hover', now),
        );
      }
      case 'fleeing': {
        const t = now - stage.at;
        const hurt = clipMs('hurt');
        const wounded = clipMs('takeoff-wounded');
        if (t < hurt) return still('hurt', clipFrame('hurt', t));
        if (t < hurt + wounded)
          return still('takeoff-wounded', clipFrame('takeoff-wounded', t - hurt));
        const k = (t - hurt - wounded) / FLEE_RISE_MS;
        if (k >= 1)
          return {
            ...still('takeoff-wounded', SHEFIRA_COMBAT_CLIPS['takeoff-wounded'].frames - 1),
            hidden: true,
          };
        const pose = sky(
          this.anchor,
          FLEE_HEIGHT * ease(k),
          'takeoff-wounded',
          SHEFIRA_COMBAT_CLIPS['takeoff-wounded'].frames - 1,
        );
        return {
          ...pose,
          alpha: 1 - k,
          shadow:
            pose.shadow === null ? null : { ...pose.shadow, alpha: pose.shadow.alpha * (1 - k) },
        };
      }
      case 'fallen': {
        const t = now - stage.at;
        const roar = clipMs('roar');
        const fade = (t - (roar - FALL_FADE_MS)) / FALL_FADE_MS;
        if (fade >= 1) return { ...still('roar', DRAGON_CLIPS.roar.frames - 1), hidden: true };
        return { ...still('roar', clipFrame('roar', t)), alpha: 1 - Math.max(0, fade) };
      }
    }
  }

  private advance(now: number): void {
    const stage = this.stage;
    // Every clip off the ground starts from the pose with her wings spread.
    if (stage.kind !== 'ground') this.gait.spread();
    if (stage.kind === 'rising' && now >= stage.at + stage.ms) {
      this.stage = { kind: 'flying', since: stage.at + stage.ms };
    } else if (stage.kind === 'landing' && now >= stage.at + stage.ms) {
      this.stage = { kind: 'ground' };
    } else if (stage.kind === 'diving') {
      const standMs = (SHEFIRA_COMBAT_CLIPS.dive.frames - 5) * SHEFIRA_COMBAT_CLIPS.dive.ms;
      if (now >= stage.landsAt + standMs + TAKEOFF_CLIP_MS + DIVE_CLIMB_MS) {
        this.stage = { kind: 'flying', since: stage.since };
      }
    }
  }
}

/**
 * Whether a point of the world (a click) lands on her body as drawn from
 * her feet: the middle of her canvas, where the dragon is, not its empty
 * corners. Her tile is far below where one aims at a dragon.
 */
export function onHerBody(feet: Point, at: Point): boolean {
  return Math.abs(at.x - feet.x) <= 64 && at.y <= feet.y + 8 && at.y >= feet.y - 150;
}
